import { ChevronDown, DatabaseBackup, LogOut, ShieldCheck, Trash2, Upload } from 'lucide-react';
import { useEffect, useRef, useState, type ChangeEvent } from 'react';

export type AccountMenuProps = {
  isOwner: boolean;
  /** The signed-in name, or null for a visitor. */
  username: string | null;
  /** Accounts awaiting approval. Always 0 for anyone but the owner. */
  pendingUserCount: number;
  onSignIn(): void;
  onSignOut(): void;
  onUpload(event: ChangeEvent<HTMLInputElement>): void;
  onOpenTrash(): void;
  onOpenAdmin(): void;
  onBackup(): void;
};

/**
 * The top-right identity slot: a sign-in button for visitors, the account menu for
 * anyone signed in. Upload lives *inside* the menu, so a visitor is never shown an
 * affordance they cannot use.
 *
 * The pending badge is repeated on the closed trigger so a waiting account is visible
 * without opening the menu first.
 */
export function AccountMenu({
  isOwner,
  username,
  pendingUserCount,
  onSignIn,
  onSignOut,
  onUpload,
  onOpenTrash,
  onOpenAdmin,
  onBackup,
}: AccountMenuProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  if (!username) {
    return (
      <div className="account">
        <button type="button" className="account-trigger is-signin" onClick={onSignIn}>
          Sign in
        </button>
      </div>
    );
  }

  return (
    <div className="account" ref={containerRef}>
      <button
        type="button"
        className="account-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <span className="account-avatar" aria-hidden="true" />
        {username}
        {pendingUserCount > 0 && (
          <span className="badge" aria-label={`${pendingUserCount} accounts waiting`}>
            {pendingUserCount}
          </span>
        )}
        <ChevronDown size={13} aria-hidden="true" />
      </button>

      {open && (
        <div className="account-menu" role="menu">
          <label role="menuitem">
            <Upload size={15} aria-hidden="true" />
            Upload MP3
            <input
              type="file"
              accept=".mp3,audio/mpeg"
              multiple
              onChange={(event) => {
                setOpen(false);
                onUpload(event);
              }}
            />
          </label>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onOpenTrash();
            }}
          >
            <Trash2 size={15} aria-hidden="true" />
            Trash
          </button>
          {isOwner && (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onOpenAdmin();
              }}
            >
              <ShieldCheck size={15} aria-hidden="true" />
              Admin
              {pendingUserCount > 0 && (
                <span className="badge">{pendingUserCount}</span>
              )}
            </button>
          )}
          {isOwner && (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onBackup();
              }}
            >
              <DatabaseBackup size={15} aria-hidden="true" />
              Back up catalog
            </button>
          )}
          <hr />
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onSignOut();
            }}
          >
            <LogOut size={15} aria-hidden="true" />
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
